import { useState } from 'react'
import { BellRing, LoaderCircle } from 'lucide-react'
import { fromPair } from '../../components/Bilingual'
import { Button } from '../../components/ui/button'
import { formatOrderId } from '../../lib/orders-api'
import type { Lang } from '../../lib/i18n'
import { createRingAudio } from './ring-audio'
import { REASON_LABEL, ringLabel } from './ring-i18n'
import { whoPlate, type RingScreen } from './ring-rules'
import { useRing, type RingState } from './useRing'

// R503 (b): what a ringing screen shows: which orders ring and why (reason and age, both
// languages, the order's short code), the "Heard it" button, and a face for each other state.

export function formatAge(ageSeconds: number) {
  const s = Math.max(0, Math.floor(ageSeconds))
  return s < 60 ? ringLabel('ringAgeSeconds', { n: String(s) }) : ringLabel('ringAgeMinutes', { n: String(Math.floor(s / 60)) })
}

function clock(ms: number): string {
  return new Date(ms).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
}

interface ViewProps {
  ring: RingState & { heard: () => void }
  size?: 'screen' | 'tv'
  // The screen's language: it picks each item's name (Rook #67688, the Cockpit card's rule).
  lang: Lang
}

export function RingView({ ring, size = 'screen', lang }: ViewProps) {
  const tv = size === 'tv'
  const line = tv ? 'text-2xl' : 'text-sm'
  if (ring.phase === 'idle') return null
  if (ring.phase === 'checking') {
    return (
      <p data-ring="checking" aria-live="polite" className={`flex items-center gap-2 text-muted-foreground ${line}`}>
        <LoaderCircle aria-hidden className="h-4 w-4 shrink-0 animate-spin" />
        {fromPair(ringLabel('ringChecking'), undefined, 'inherit')}
      </p>
    )
  }
  if (ring.phase === 'failed') {
    return (
      <p
        data-ring="failed"
        aria-live="assertive"
        className={`rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 font-medium text-destructive ${line}`}
      >
        {fromPair(ringLabel('ringFailed'), undefined, 'inherit')}
      </p>
    )
  }
  if (!ring.on) {
    return (
      <p data-ring="off" className={`text-muted-foreground ${line}`}>
        {fromPair(ringLabel('ringOff'), undefined, 'inherit')}
      </p>
    )
  }
  if (ring.rows.length === 0) {
    return (
      <p data-ring="none" className={`text-muted-foreground ${line}`}>
        {fromPair(ringLabel('ringNone'), undefined, 'inherit')}
      </p>
    )
  }
  const paused = ring.pausedUntil !== null
  return (
    <section
      data-ring="ringing"
      data-sounding={ring.sounding ? 'true' : 'false'}
      aria-labelledby={`ring-title-${size}`}
      className="flex flex-col gap-3 rounded-xl border-2 border-destructive bg-destructive-tint p-4 text-foreground"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id={`ring-title-${size}`} className={`flex items-center gap-2 font-semibold text-destructive ${tv ? 'text-4xl' : 'text-lg'}`}>
          <BellRing aria-hidden className={tv ? 'h-10 w-10 shrink-0' : 'h-5 w-5 shrink-0'} />
          {fromPair(ringLabel('ringTitle'), undefined, 'inherit')}
        </h2>
        <Button
          type="button"
          data-ring="heard"
          disabled={paused}
          aria-describedby={paused ? `ring-paused-${size}` : undefined}
          onClick={ring.heard}
          className={`min-h-[44px] gap-2 font-semibold ${tv ? 'h-16 px-8 text-2xl' : 'px-5 text-base'}`}
        >
          {fromPair(ringLabel('ringHeard'), undefined, 'inherit')}
        </Button>
      </div>
      {paused ? (
        <p id={`ring-paused-${size}`} data-ring="paused" className={`text-muted-foreground ${line}`}>
          {fromPair(ringLabel('ringPaused', { time: clock(ring.pausedUntil!) }), undefined, 'inherit')}
        </p>
      ) : null}
      {ring.quiet ? (
        <p data-ring="quiet" className={`text-muted-foreground ${line}`}>
          {fromPair(ringLabel('ringQuiet'), undefined, 'inherit')}
        </p>
      ) : null}
      <ul className="flex flex-col divide-y divide-border">
        {ring.rows.map((row) => ({ row, who: whoPlate(row, lang) })).map(({ row, who }) => (
          <li key={row.id} data-ring-order={row.id} className={`flex flex-wrap items-baseline gap-x-4 gap-y-1 py-2 ${tv ? 'text-3xl' : 'text-base'}`}>
            {who !== null ? (
              // Rook #67688: the person and the plate lead; the code follows, smaller, for lookups.
              <>
                <span data-ring="who" className="font-bold">
                  {fromPair(ringLabel('ringWhoPlate', { name: who.name, plate: who.plate }), undefined, 'inherit')}
                </span>
                <span dir="ltr" data-ring="code" className={`text-muted-foreground ${tv ? 'text-xl' : 'text-sm'}`}>
                  {formatOrderId(row.id, row.shortCode)}
                </span>
              </>
            ) : (
              <span dir="ltr" data-ring="code" className="font-bold">
                {formatOrderId(row.id, row.shortCode)}
              </span>
            )}
            {row.reasons.map((r) => (
              <span key={r.reason} data-ring-reason={r.reason} className="inline-flex flex-wrap items-baseline gap-2">
                {fromPair(ringLabel(REASON_LABEL[r.reason]), undefined, 'inherit')}
                <span data-ring="age" className="tabular-nums text-muted-foreground">
                  {fromPair(formatAge(r.ageSeconds), undefined, 'inherit')}
                </span>
              </span>
            ))}
          </li>
        ))}
      </ul>
    </section>
  )
}

interface PanelProps {
  screen: RingScreen
  lang: Lang
}

// The ring on Cockpit, Board and the kitchen phone: the poll loop runs from mount and lists who
// waits; a tap turns the sound on (the browser lets a page make sound only after a tap). The
// kitchen TV arms from its own start overlay and draws RingView itself.
export function RingPanel({ screen, lang }: PanelProps) {
  const [audio, setAudio] = useState<AudioContext | null>(null)
  const [arming, setArming] = useState(false)
  const [blocked, setBlocked] = useState(false)
  const ring = useRing(screen, audio)

  async function arm() {
    setArming(true)
    const ctx = createRingAudio()
    if (ctx) {
      try {
        await ctx.resume()
      } catch {
        // fall through: the state check below decides
      }
    }
    setArming(false)
    if (ctx && ctx.state === 'running') {
      setBlocked(false)
      setAudio(ctx)
    } else {
      setBlocked(true)
    }
  }

  return (
    <div data-ring-panel={screen} dir={lang === 'ar' ? 'rtl' : 'ltr'} className="flex flex-col gap-2">
      {audio === null ? (
        // Rook #67784 (2): the list below shows before the tap; only the sound waits for it.
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            variant="outline"
            data-ring="arm"
            disabled={arming}
            aria-busy={arming}
            onClick={() => void arm()}
            className="min-h-[44px] gap-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
          >
            {arming ? <LoaderCircle aria-hidden className="animate-spin" /> : <BellRing aria-hidden />}
            {fromPair(ringLabel(arming ? 'ringArming' : 'ringArm'), undefined, 'inherit')}
          </Button>
          <span className="text-sm text-muted-foreground">{fromPair(ringLabel('ringArmHint'), undefined, 'inherit')}</span>
          {blocked ? (
            <p data-ring="blocked" aria-live="assertive" className="w-full text-sm font-medium text-destructive">
              {fromPair(ringLabel('ringBlocked'), undefined, 'inherit')}
            </p>
          ) : null}
        </div>
      ) : null}
      <RingView ring={ring} lang={lang} />
    </div>
  )
}
